import { type FC, useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import {useConsent} from "../context/ConsentContext";

const ProfilePage: FC = () => {
    const { resetConsentChoice } = useConsent();
    const { user, fetchUser } = useAuth();

    const [profileDetails, setProfileDetails] = useState({
        name: '',
        surname: '',
        email: ''
    });

    const [passwordDetails, setPasswordDetails] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (user) {
            setProfileDetails({
                name: user.name || '',
                surname: user.surname || '',
                email: user.email || ''
            });
        }
    }, [user]);

    const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setProfileDetails({ ...profileDetails, [e.target.id]: e.target.value });
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPasswordDetails({ ...passwordDetails, [e.target.id]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setSuccessMessage(null);
        setErrorMessage(null);

        try {
            await api.put('/api/users/update-details', {
                name: profileDetails.name,
                surname: profileDetails.surname,
                email: profileDetails.email
            });

            if (passwordDetails.newPassword) {
                await api.put('/api/users/change-password', passwordDetails);
            }

            setSuccessMessage("Profilo aggiornato con successo!");
            setPasswordDetails({ currentPassword: '', newPassword: '', confirmPassword: '' });
            fetchUser();

        } catch (err: any) {
            const message = err.response?.data?.message || err.message || "Si è verificato un errore.";
            setErrorMessage(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <h1 className="text-3xl font-bold text-white mb-2">Profilo Utente</h1>
            <p className="text-slate-400 mb-8">Modifica le tue informazioni personali e la password.</p>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 max-w-2xl">
                <form className="space-y-6" onSubmit={handleSubmit}>
                    {/* Dettagli Profilo */}
                    <div>
                        <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">Nome</label>
                        <input type="text" id="name" value={profileDetails.name} onChange={handleProfileChange} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
                    </div>
                    <div>
                        <label htmlFor="surname" className="block text-sm font-medium text-slate-300 mb-2">Cognome</label>
                        <input type="text" id="surname" value={profileDetails.surname} onChange={handleProfileChange} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                        <input type="email" id="email" value={profileDetails.email} onChange={handleProfileChange} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
                    </div>

                    {/* Cambia Password */}
                    <div className="border-t border-slate-700 pt-6">
                        <h3 className="text-lg font-medium text-white mb-4">Cambia Password</h3>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="currentPassword" className="block text-sm font-medium text-slate-300 mb-2">Password Corrente</label>
                                <input type="password" id="currentPassword" value={passwordDetails.currentPassword} onChange={handlePasswordChange} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
                            </div>
                            <div>
                                <label htmlFor="newPassword" className="block text-sm font-medium text-slate-300 mb-2">Nuova Password</label>
                                <input type="password" id="newPassword" value={passwordDetails.newPassword} onChange={handlePasswordChange} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
                            </div>
                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-2">Conferma Nuova Password</label>
                                <input type="password" id="confirmPassword" value={passwordDetails.confirmPassword} onChange={handlePasswordChange} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"/>
                            </div>
                        </div>
                    </div>

                    {/* Feedback e Pulsante di Invio */}
                    <div className="pt-4">
                        {successMessage && <div className="text-center p-3 mb-4 bg-green-500/20 text-green-300 rounded-lg">{successMessage}</div>}
                        {errorMessage && <div className="text-center p-3 mb-4 bg-red-500/20 text-red-300 rounded-lg">{errorMessage}</div>}
                        <div className="flex justify-end">
                            <button type="submit" disabled={isLoading} className="px-6 py-2 rounded-lg text-white font-semibold bg-sky-600 hover:bg-sky-500 transition-colors disabled:bg-slate-600 flex items-center">
                                {isLoading && <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                                {isLoading ? 'Salvataggio...' : 'Salva Modifiche'}
                            </button>
                        </div>
                    </div>
                </form>
                <br/>
                <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-lg">
                    <h2 className="text-xl font-semibold text-white">Impostazioni Privacy</h2>
                    <p className="text-slate-300 mt-2 mb-4">
                        Puoi modificare in qualsiasi momento le tue preferenze relative al consenso per la raccolta dei dati.
                    </p>
                    <button
                        onClick={resetConsentChoice}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300"
                    >
                        Gestisci Consenso
                    </button>
                </div>

            </div>

        </div>
    );
}

export default ProfilePage;
